import { memo, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import AddToBagButton from 'components/AddToBagButton';
import cartSlice from 'stores/reducers/cart';
import getSkuSuffix from 'utils/get-sku-suffix';
import styles from './ProductAddToBag.module.scss';

const MAX_QUANTITY = 25;

function ProductAddToBag({
  batchSKU,
  brand,
  category,
  currencyCode,
  currentPriceCents,
  productName,
  quantityAvailable,
  regularPriceCents,
  SKU,
  salesTaxDisclosure,
  selectedMenu,
  sourceId,
  sourceName,
  storeSelection,
  subcategory,
  variation,
}) {
  const dispatch = useDispatch();
  const { lineItems, limitError } = useSelector(({ cart }) => cart);
  const [errorCount, setErrorCount] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const maxSelection =
    quantityAvailable < MAX_QUANTITY ? quantityAvailable : MAX_QUANTITY;
  const suffix = getSkuSuffix(category, selectedMenu);

  const lineItem = lineItems.find((item) => item.batchSKU === batchSKU);

  useEffect(() => {
    dispatch(cartSlice.actions.setCartLimitError(null));
  }, [dispatch, storeSelection]);

  const onError = useCallback((isError) => {
    if (isError) {
      setErrorCount((e) => e + 1);
    } else {
      setErrorCount(0);
    }
  }, []);

  return (
    <div className="mb-10 mb-lg-15">
      {maxSelection < MAX_QUANTITY ? (
        <div
          className="fst-italic fw-600 text-danger mb-15"
          data-value="product-limited-quantity-message"
        >
          Only {maxSelection} left at this store.
        </div>
      ) : null}
      <div className="d-flex flex-row align-items-start">
        <AddToBagButton
          showError
          availableQuantity={maxSelection}
          batchSKU={batchSKU}
          brand={brand}
          cartQuantity={lineItem?.quantity ?? 0}
          category={category}
          className={classNames('me-6 mt-0', {
            [styles.button]: !lineItem?.quantity || lineItem?.quantity === 0,
            'w-100': lineItem?.quantity > 0,
          })}
          currencyCode={currencyCode}
          currentPriceCents={currentPriceCents}
          productName={productName}
          quantity={quantity}
          regularPriceCents={regularPriceCents}
          showIncrementButtons={false}
          SKU={`${SKU}${suffix}`}
          sourceId={sourceId}
          sourceName={sourceName}
          storeSelection={storeSelection}
          subcategory={subcategory}
          variation={variation}
          onError={onError}
        />
        {!(lineItem?.quantity > 0) && (
          <select
            aria-label="Quantity"
            className={`rounded-0 text-center text-primary ${styles.quantity}`}
            onChange={(e) =>
              setQuantity(Number.parseInt(e.currentTarget.value, 10))
            }
          >
            {[...Array(maxSelection).keys()].map((idx) => (
              <option key={idx} value={idx + 1}>
                {idx + 1}
              </option>
            ))}
          </select>
        )}
      </div>
      {limitError?.message && (
        <p
          role="alert"
          aria-live="assertive"
          className="fst-italic text-danger my-4"
        >
          {limitError?.message}
        </p>
      )}
      {errorCount === 1 ? (
        <p
          role="alert"
          aria-live="assertive"
          className="fst-italic text-danger my-4"
        >
          An unexpected error occurred. Please try again.
        </p>
      ) : null}
      {errorCount > 1 ? (
        <p
          role="alert"
          aria-live="assertive"
          className="fst-italic text-danger my-4"
        >
          An unexpected error occurred. Please try again. If you’re still
          receiving this error message, please text (947.221.8691) or call
          (888.208.7599) our customer care team to help process your order
          immediately.
        </p>
      ) : null}
      <span
        className="fs-6 fw-400 mt-3 text-grey-darker"
        data-value="tax-disclosure"
      >
        {salesTaxDisclosure}
      </span>
    </div>
  );
}

export default memo(ProductAddToBag);
